import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import type { DashboardSettingsPatch } from "@/types/dashboard";
import { slugify } from "@/lib/dashboardStore";

const SLUG_RE = /^[a-z0-9-]*$/;

export function DashboardSettingsModal({
  open,
  onOpenChange,
  name: nameProp,
  description: descriptionProp,
  slug: slugProp,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  name: string;
  description?: string;
  slug: string;
  onSave: (patch: DashboardSettingsPatch) => void | Promise<void>;
}) {
  const [name, setName] = useState(nameProp);
  const [description, setDescription] = useState(descriptionProp ?? "");
  const [slug, setSlug] = useState(slugProp);

  useEffect(() => {
    setName(nameProp);
    setDescription(descriptionProp ?? "");
    setSlug(slugProp);
  }, [nameProp, descriptionProp, slugProp, open]);

  function handleSlugChange(value: string) {
    if (SLUG_RE.test(value)) setSlug(value);
  }

  function handleSave() {
    const finalSlug = slug.trim() || slugify(name.trim()) || slugProp;
    onSave({
      name: name.trim() || "Dashboard",
      description: description.trim() || undefined,
      slug: finalSlug,
    });
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-8 flex flex-col gap-6">
        <DialogTitle className="text-base font-semibold text-primary">
          Dashboard Settings
        </DialogTitle>
        <DialogDescription className="sr-only">
          Edit the name, description and slug of this dashboard.
        </DialogDescription>

        {/* Form */}
        <div className="flex flex-col gap-5">
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-secondary uppercase tracking-wide">Name</span>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSave()}
              placeholder="My Dashboard"
              maxLength={64}
            />
          </label>

          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-secondary uppercase tracking-wide">
              URL slug{" "}
              <span className="normal-case font-normal text-muted tracking-normal">
                (monitoring URL)
              </span>
            </span>
            <div className="flex items-center rounded-lg border border-border bg-surface-elevated overflow-hidden focus-within:ring-2 focus-within:ring-brand-cyan/30 focus-within:border-brand-cyan/50">
              <span className="pl-3 pr-1 text-xs text-muted select-none whitespace-nowrap shrink-0">
                /monitoring/
              </span>
              <input
                className="flex-1 bg-transparent py-2 pr-3 text-sm text-primary outline-none placeholder:text-muted/50 min-w-0"
                value={slug}
                onChange={(e) => handleSlugChange(e.target.value)}
                onBlur={() => !slug.trim() && setSlug(slugify(name.trim()) || slugProp)}
                placeholder="my-dashboard"
                maxLength={60}
                spellCheck={false}
              />
            </div>
            <p className="text-[11px] text-muted leading-snug">
              Lowercase letters, digits and hyphens only. Changing this may break existing
              bookmarks.
            </p>
          </div>

          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-secondary uppercase tracking-wide">
              Description <span className="normal-case font-normal text-muted">(optional)</span>
            </span>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="A short description of this dashboard…"
              rows={3}
              maxLength={256}
            />
          </label>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave}>
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
